/** Every type here comes from the generated contract, so a field added on one
 *  side is a type error on the other rather than a silent drop. */
import { request } from '@/api/client'
import type {
  CalendarEntryResponse,
  CallerConvertResponse,
  CallerResponse,
  CallerStatus,
  CallerWriteRequest,
  Paginated,
  PatientWriteRequest,
  TravelPlanResponse,
  TravelPlanWriteRequest,
} from '@/api/types'

export const callers = {
  list: (
    status: CallerStatus | 'all',
    q: string,
    limit: number,
    cursor: string | null,
    signal?: AbortSignal,
  ) =>
    request<Paginated<CallerResponse>>('/v1/callers', {
      query: {
        status: status === 'all' ? undefined : status,
        q: q || undefined,
        limit,
        cursor: cursor ?? undefined,
      },
      ...(signal ? { signal } : {}),
    }),
  get: (id: string, signal?: AbortSignal) =>
    request<CallerResponse>(`/v1/callers/${id}`, signal ? { signal } : {}),
  due: (limit: number, signal?: AbortSignal) =>
    request<Paginated<CallerResponse>>('/v1/callers/due', {
      query: { limit },
      ...(signal ? { signal } : {}),
    }),
  counts: (signal?: AbortSignal) =>
    request<{ counts: Record<string, number> }>('/v1/callers/counts', signal ? { signal } : {}),
  create: (body: CallerWriteRequest) =>
    request<CallerResponse>('/v1/callers', { method: 'POST', body }),
  update: (id: string, body: CallerWriteRequest) =>
    request<CallerResponse>(`/v1/callers/${id}`, { method: 'PATCH', body }),
  /** Registers the patient AND closes the enquiry, in one request. */
  convert: (id: string, body: PatientWriteRequest) =>
    request<CallerConvertResponse>(`/v1/callers/${id}/convert`, { method: 'POST', body }),
}

export const travel = {
  list: (from: string, to: string, signal?: AbortSignal) =>
    request<Paginated<TravelPlanResponse>>('/v1/travel', {
      query: { from, to },
      ...(signal ? { signal } : {}),
    }),
  locations: (signal?: AbortSignal) =>
    request<Paginated<string>>('/v1/travel/locations', signal ? { signal } : {}),
  create: (body: TravelPlanWriteRequest) =>
    request<TravelPlanResponse>('/v1/travel', { method: 'POST', body }),
  update: (id: string, body: TravelPlanWriteRequest) =>
    request<TravelPlanResponse>(`/v1/travel/${id}`, { method: 'PATCH', body }),
  remove: (id: string) => request<void>(`/v1/travel/${id}`, { method: 'DELETE' }),
}

export const calendar = {
  month: (from: string, to: string, signal?: AbortSignal) =>
    request<Paginated<CalendarEntryResponse>>('/v1/calendar', {
      query: { from, to },
      ...(signal ? { signal } : {}),
    }),
}

/** Label and tone for each status, in one place so the list and the form agree. */
export const CALLER_STATUS_META: Record<
  CallerStatus,
  { label: string; tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger' }
> = {
  new_call: { label: 'New call', tone: 'warning' },
  follow_up: { label: 'Follow-up', tone: 'info' },
  appointment_booked: { label: 'Appointment booked', tone: 'success' },
  became_patient: { label: 'Became patient', tone: 'neutral' },
  no_appointment: { label: 'No appointment', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'danger' },
}

/** Everything a doctor can set by hand. "Became patient" is convert-only. */
export const EDITABLE_STATUSES: CallerStatus[] = [
  'new_call',
  'follow_up',
  'appointment_booked',
  'no_appointment',
  'cancelled',
]
